import { css } from '@emotion/core';
import { useAlert } from '@weave/alert-system';
import {
  ButtonBar,
  CheckboxField,
  ContentLoader,
  DatePickerField,
  DropdownField,
  EditIcon,
  FormRow,
  IconButton,
  Modal,
  PhoneField,
  PlusIcon,
  PrimaryButton,
  SecondaryButton,
  Text,
  TextField,
  TrashIcon,
  useForm,
  useFormField,
  useModalControl,
  ValidatorFieldState,
} from '@weave/design-system';
import {
  MediaData,
  PortInCreateByPortingDataIDResponse,
  PortingData,
  PortingRequest,
} from '@weave/schema-gen-ts/dist/schemas/phone/porting/porting-data/v1/porting_data_service.pb';
import {
  NumberType,
  PortingType,
  PortStatus,
  PortType,
  Provider,
} from '@weave/schema-gen-ts/dist/shared/porting/v1/enums.pb';
import { PortingDataService } from '@weave/schema-gen-ts/dist/schemas/phone/porting/porting-data/v1/porting_data_service.pb';
import { AgreementContentService } from '@weave/schema-gen-ts/dist/schemas/agreementcontent/v1/agreementcontent.pb';
import { InsysAgreementsAPI } from '@weave/schema-gen-ts/dist/schemas/agreements/v1/insys_agreements.pb';
import { authorizedFetch } from 'helpers/utils';
import { theme } from '@weave/theme-original';
import { MediaType, PortingMediaType } from 'apis/porting/porting.types';
import { iconButtonSvgPrimary } from 'components/location-sidebar-container/location-sidebar.styles';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { chunk, cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getUser } from 'redux/actions/auth/user/user.action';
import { getParsedErrorMessage } from 'redux/axios';
import { Store } from 'redux/store/store.model';
import { portingSchemaApi } from '../../../apis/porting/porting.api';
import { PortingQueries } from '../../../apis/porting/porting.queries';
import { injectParams, ONBOARDING } from '../../../constants/paths';
import { DataCenters, selectCurrentLocation } from '../../../redux/actions/location';
import { useDebugMode } from '../../debug/use-debug-mode';
import { Page } from '../../shared';
import { Loader } from '../../shared/loader/loader.component';
import { useDeletePortingData } from '../hooks/use-delete-porting-data';
import { useDownloadLoa } from '../hooks/use-download-loa';
import { SuccessSVG } from '../porting-hub/success-svg';
import { MessageDialogue } from '../utils/message-dialogue.component';
import { CountryCodes } from '../utils/port-constants';
import {
  buildRequestedNumberTypesMap,
  convertToDateWithSpecificTime,
  convertUsDateToIsoDateFormat,
  filterPortingValidations,
  getConflictingExistingNumbers,
  getErrorMessageFromResponseError,
  getFormattedDate,
  getPortingDataObjectList,
  getPortingStatusColor,
  getPortOrderType,
  getStatusForPorting,
  getUTCDateWithPortingTimeString,
  isEqualStringUnorderedArray,
  isFaxDIDNumber,
  isHoliday,
  isVoiceSmsDIDNumber,
  isWeekend,
  validatePhoneNumbersRegex,
} from '../utils/porting-utils';
import { DeletePortingDataButton } from './delete-porting-data-button';
import { portingValidationError } from './error-code';
import { PortingCardAttachments } from './port-card-attachments.component';
import { PortingCardOfficeInfo } from './porting-card-office-info.component';
import { portingCardStyles } from './porting-card.styles';
import { SupRequestModal } from './sup-request-modal';
import { PortAuditTable } from '../port-audit/PortAuditTable';
import { useQueryClient } from 'react-query';
import { AxiosError } from 'axios';

type MessageModal = {
  header: string;
  body: string;
  success: boolean;
  actionLabel: string;
  onClose: () => void;
  onActionLabelClick: () => void;
};

interface PortingCardProps {
  port: PortingData;
  onBack: () => void;
  setPortingDetails: (port?: PortingData) => void;
}

const MIN_PHONE_NUMBER_LENGTH = 10;

export const PortingCard = ({ port, onBack, setPortingDetails }: PortingCardProps) => {
  const alert = useAlert();
  const [portOrderUrl, setPortOrderUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [duplicateError, setDuplicateError] = useState<string>('');
  const [nonHostableNumbersError, setNonHostableNumbersError] = useState<string>('');
  const [phoneNumberFormatError, setPhoneNumberFormatError] = useState<string>('');
  const [portingInfoEdit, setPortingInfoEdit] = useState<boolean>(false);
  const [phoneNumbersEdit, setPhoneNumbersEdit] = useState<boolean>(false);
  const [officeInfoEdit, setOfficeInfoEdit] = useState<boolean>(false);
  const [canEditAttachments, setCanEditAttachments] = useState<boolean>(false);
  const [portingInfoError, setPortingInfoError] = useState<string>('');
  const [portingProviderError, setPortingProviderError] = useState<string>();
  const [missingRequestedPort, setMissingReqestedPort] = useState(false);
  const [missingPortingCompany, setMissingPortingCompany] = useState(false);

  const {
    modalProps: phoneNumbersEditDiscardModalProps,
    openModal: openPhoneNumbersEditDiscardModal,
    closeModal: closePhoneNumbersEditDiscardModal,
  } = useModalControl();

  const queryClient = useQueryClient();
  const history = useHistory();

  const currentLocation = useSelector(selectCurrentLocation);

  const invalidatePortingData = (portingDataId?: string) => {
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        return (
          queryKey.includes(PortingQueries.PortingQueryKeys.baseQueryKey[0]) &&
          !!portingDataId &&
          queryKey.includes(portingDataId)
        );
      },
    });
  };

  const { mutateAsync: updatePortingData, isLoading: isUpdatingPortingData } =
    PortingQueries.useUpdatePortingData({
      onError: (error) => {
        alert.error(getParsedErrorMessage(error) ?? 'Error updating porting data');
      },
      onSuccess: (data) => {
        invalidatePortingData(data?.portingData?.id);
        setPortingDetails(data?.portingData);
      },
    });

  const {
    modalProps: messageModalProps,
    openModal: openMessageModal,
    closeModal: closeMessageModal,
  } = useModalControl();

  const {
    modalProps: cancelModalProps,
    closeModal: closeCancelModal,
    openModal: openCancelModal,
  } = useModalControl();

  const { modalProps: supModalProps, openModal: openSUPModal } = useModalControl();

  const [messagePortModal, setMessagePortModal] = useState<MessageModal>({
    header: '',
    body: '',
    success: true,
    actionLabel: 'Okay',
    onClose: () => {},
    onActionLabelClick: () => {},
  });
  const [duplicateNumberError, setDuplicateNumberError] = useState<boolean>(false);
  const dispatch = useDispatch();
  const username = useSelector((state: Store) => state.auth.user.username);
  const userId = useSelector((state: Store) => state.auth.user.user_id);

  const { isDebugModeEnabled } = useDebugMode();

  const { deletePortingData } = useDeletePortingData();

  const { mutateAsync: checkSmsHostabilityBatch } =
    PortingQueries.useCheckSmsHostabilityBatchMutation();

  const { mutate: createSFPortOrder, isLoading: isSFPortOrderMutating } =
    PortingQueries.useCreateSFPortOrder({
      onSuccess: () => {
        setMessagePortModal({
          body: 'Successfully sent to salesforce',
          header: 'Success',
          success: false,
          actionLabel: 'Okay',
          onClose: closeMessageModal,
          onActionLabelClick: closeMessageModal,
        });
      },
      onError: (error) => {
        console.error(error);
        setMessagePortModal({
          header: 'Failed',
          body: getParsedErrorMessage(error),
          success: false,
          actionLabel: 'Okay',
          onClose: closeMessageModal,
          onActionLabelClick: closeMessageModal,
        });
      },
      onSettled: () => {
        openMessageModal();
      },
    });

  const { mutate: clonePortOrder, isLoading: isClonePortMutating } =
    PortingQueries.useClonePortOrder({
      onSuccess: (response) => {
        setMessagePortModal({
          body: 'Port order cloned. Open cloned port order',
          header: 'Success',
          success: true,
          actionLabel: 'Okay',
          onClose: closeMessageModal,
          onActionLabelClick: (): void => {
            closeMessageModal();
            goToPortOrderList(response?.portingData?.id);
          },
        });
      },
      onError: (error) => {
        console.error(error);
        setMessagePortModal({
          header: 'Failed',
          body: getParsedErrorMessage(error),
          success: false,
          actionLabel: 'Okay',
          onClose: closeMessageModal,
          onActionLabelClick: closeMessageModal,
        });
      },
      onSettled: () => {
        openMessageModal();
      },
    });

  const handlePortOrderCancelSuccess = () => {
    const successMsg =
      portingRequest?.portOrderNumber &&
      !portingRequest?.portOrderNumber.includes('Cancelled')
        ? 'Requested cancellation of port order'
        : 'Port order cancelled';

    setMessagePortModal({
      body: successMsg,
      header: 'Success',
      success: true,
      actionLabel: 'Okay',
      onClose: closeMessageModal,
      onActionLabelClick: () => {
        closeMessageModal();
        onBack();
      },
    });
  };

  const handlePortOrderCancelError = (error: AxiosError) => {
    console.error(error);
    setMessagePortModal({
      header: 'Failed',
      body: getParsedErrorMessage(error),
      success: false,
      actionLabel: 'Okay',
      onClose: closeMessageModal,
      onActionLabelClick: closeMessageModal,
    });
  };

  const handlePortOrderCancelMutationComplete = () => {
    closeCancelModal();
    // Timeout added to avoid scroll issue if another modal is opened immediately after closing first one
    setTimeout(() => {
      openMessageModal();
    }, 1000);
  };

  const { mutate: cancelPortOrderMutation, isLoading: isCancelMutating } =
    PortingQueries.useCancelPortOrder({
      onSuccess: handlePortOrderCancelSuccess,
      onError: handlePortOrderCancelError,
      onSettled: handlePortOrderCancelMutationComplete,
    });

  const { mutate: cancelSMSPortOrderMutation, isLoading: isCancelSMSMutating } =
    PortingQueries.useCancelSMSPortOrder({
      onSuccess: handlePortOrderCancelSuccess,
      onError: handlePortOrderCancelError,
      onSettled: handlePortOrderCancelMutationComplete,
    });

  const { data: portingValidationData } = PortingQueries.useGetPortingValidationData(
    {
      portingDataId: port.id,
    },
    {
      enabled:
        port?.portingRequests?.[0].portingStatus ===
        PortStatus.PORT_STATUS_VALIDATION_FAILED,
      onError: (error) => {
        console.error(error);
        setError(
          `Unable to fetch the validation data,please try again ${getParsedErrorMessage(
            error
          )}`
        );
      },
    }
  );

  const {
    documentError,
    officeInformationError,
    portingInformationError,
    phoneNumberError,
  } = useMemo(() => {
    return {
      portingInformationError: filterPortingValidations(
        portingValidationData,
        portingValidationError.porting_information_error.min_error_code,
        portingValidationError.porting_information_error.max_error_code
      ),
      officeInformationError: filterPortingValidations(
        portingValidationData,
        portingValidationError.office_information_error.min_error_code,
        portingValidationError.office_information_error.max_error_code
      ),
      documentError: filterPortingValidations(
        portingValidationData,
        portingValidationError.document_error.min_error_code,
        portingValidationError.document_error.max_error_code
      ),
      phoneNumberError: filterPortingValidations(
        portingValidationData,
        portingValidationError.phone_information_error.min_error_code,
        portingValidationError.phone_information_error.max_error_code
      ),
    };
  }, [portingValidationData, portingValidationError]);

  useEffect(() => {
    dispatch(getUser({ username }));
  }, [username]);

  const { downloadLoa, isLoading: downloadLOALoading } = useDownloadLoa();

  const existingNumberValues: PortingRequest[] = [];
  const existingFaxValue: PortingRequest[] = [];

  port?.portingRequests?.forEach(
    (portingRequest) =>
      isVoiceSmsDIDNumber(portingRequest.numberType) &&
      existingNumberValues.push(portingRequest)
  );

  port?.portingRequests?.forEach(
    (portingRequest) =>
      isFaxDIDNumber(portingRequest.numberType) && existingFaxValue.push(portingRequest)
  );

  const [newPhoneNumbersList, setNewPhoneNumbersList] =
    useState<PortingRequest[]>(existingNumberValues);
  const [newFaxNumbersList, setNewFaxNumbersList] =
    useState<PortingRequest[]>(existingFaxValue);

  const [existingPortedNumbers, setExistingPortedNumbers] = useState<string[]>([]);

  const providerDropdownProps = useFormField({
    type: 'text',
    value: port.provider ? port.provider : Provider.PROVIDER_UNSPECIFIED,
  });

  const getPhoneNumbers = (portingRequests: PortingRequest[]): string[] =>
    portingRequests?.map((portingRequest) => portingRequest?.phoneNumber ?? '');

  const hasUnsavedPhoneNumberChanges = useMemo(() => {
    return (
      !isEqualStringUnorderedArray(
        getPhoneNumbers(existingNumberValues),
        getPhoneNumbers(newPhoneNumbersList)
      ) ||
      !isEqualStringUnorderedArray(
        getPhoneNumbers(existingFaxValue),
        getPhoneNumbers(newFaxNumbersList)
      )
    );
  }, [existingNumberValues, existingFaxValue, newPhoneNumbersList, newFaxNumbersList]);

  const isPhoneNumbersSaveDisabled = useMemo(() => {
    if (existingPortedNumbers.length > 0) {
      return true;
    }
    if (duplicateNumberError) {
      return true;
    }
    const phoneNumbersLengthsArray = (newPhoneNumbersList ?? [])?.map(
      (data) => data?.phoneNumber?.length
    );
    const faxNumbersLengthsArray = (newFaxNumbersList ?? [])?.map(
      (data) => data?.phoneNumber?.length
    );

    if (!faxNumbersLengthsArray.length && !phoneNumbersLengthsArray.length) {
      return true;
    }

    if (!hasUnsavedPhoneNumberChanges) {
      return true;
    }

    const hasInvalidNumbers = [
      ...phoneNumbersLengthsArray,
      ...faxNumbersLengthsArray,
    ]?.some((el) => (el ?? 0) < MIN_PHONE_NUMBER_LENGTH);

    return hasInvalidNumbers;
  }, [
    existingPortedNumbers,
    duplicateNumberError,
    newPhoneNumbersList,
    newFaxNumbersList,
    hasUnsavedPhoneNumberChanges,
  ]);

  const getTypeOfPortingRequest = (): string => {
    switch (port?.portType) {
      case PortingType.PORTING_TYPE_MAIN:
        return 'Main';
      case PortingType.PORTING_TYPE_INTERNAL:
        return 'Internal';
      default:
        return 'Unknown';
    }
  };

  useEffect(() => {
    const getPortingOrderUrl = async () => {
      if (!port.id || !port.provider || !portingRequest?.portOrderNumber) return;

      try {
        const result = await portingSchemaApi.getPortOrderUrl(port.id);
        setPortOrderUrl(result.portOrderUrl ?? '');
      } catch (error) {
        alert.error(`Error fetching the port order URL ${error}`);
      }
    };

    getPortingOrderUrl();
  }, []);

  const findDuplicateValue = (arr1: string[], arr2: string[]): string[] => {
    return arr1?.filter((arr1Item) => arr2.includes(arr1Item));
  };

  useEffect(() => {
    const newPhnArray = newPhoneNumbersList?.map((phnData) => {
      return phnData?.phoneNumber ?? '';
    });
    const newFaxArray = newFaxNumbersList?.map((phnData) => {
      return phnData?.phoneNumber ?? '';
    });
    const result = findDuplicateValue(newPhnArray, newFaxArray);
    setDuplicateNumberError(!!result?.length);
  }, [newPhoneNumbersList]);

  useEffect(() => {
    const newPhnArray = newPhoneNumbersList?.map((phnData) => {
      return phnData?.phoneNumber ?? '';
    });
    const newFaxArray = newFaxNumbersList?.map((phnData) => {
      return phnData?.phoneNumber ?? '';
    });
    const result = findDuplicateValue(newPhnArray, newFaxArray);
    setDuplicateNumberError(!!result?.length);
  }, [newFaxNumbersList]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [missingRequestedPort, missingPortingCompany]);

  const handleNewNumberAddition = (numberType: NumberType) => {
    setDuplicateError('');
    setNonHostableNumbersError('');
    setPhoneNumberFormatError('');
    setExistingPortedNumbers([]);
    if (isVoiceSmsDIDNumber(numberType)) {
      setNewPhoneNumbersList([
        ...newPhoneNumbersList,
        {
          phoneNumber: '',
          portType: PortType.PORT_TYPE_UNSPECIFIED,
          numberType,
          portingDataId: port.id,
          requestedFirmOrderCommitmentDate:
            port.portingRequests?.[0].requestedFirmOrderCommitmentDate,
        },
      ]);
    } else if (isFaxDIDNumber(numberType)) {
      setNewFaxNumbersList([
        ...newFaxNumbersList,
        {
          phoneNumber: '',
          portType: PortType.PORT_TYPE_UNSPECIFIED,
          numberType: NumberType.NUMBER_TYPE_FAX,
          portingDataId: port.id,
          requestedFirmOrderCommitmentDate:
            port.portingRequests?.[0].requestedFirmOrderCommitmentDate,
        },
      ]);
    }
  };

  const handleNumberDeletion = (findIndex, numberType) => {
    setDuplicateError('');
    setNonHostableNumbersError('');
    setPhoneNumberFormatError('');
    setExistingPortedNumbers([]);
    if (isVoiceSmsDIDNumber(numberType)) {
      setNewPhoneNumbersList(
        newPhoneNumbersList.filter((_, index) => index !== findIndex)
      );
    } else if (isFaxDIDNumber(numberType)) {
      setNewFaxNumbersList(newFaxNumbersList.filter((_, index) => index !== findIndex));
    }
  };

  const handleNumberChange = (value, index, numberType) => {
    setDuplicateError('');
    setNonHostableNumbersError('');
    setPhoneNumberFormatError('');
    setExistingPortedNumbers([]);
    const numberValue = value.value;
    const isVoiceSmsNumber = isVoiceSmsDIDNumber(numberType);
    const isFaxNumber = isFaxDIDNumber(numberType);
    if (isVoiceSmsNumber || isFaxNumber) {
      const list = isVoiceSmsNumber ? [...newPhoneNumbersList] : [...newFaxNumbersList];
      list[index].phoneNumber = numberValue;
      isVoiceSmsNumber ? setNewPhoneNumbersList(list) : setNewFaxNumbersList(list);
    }
  };

  const today = new Date();
  const minDate = format(new Date(), 'MM/dd/yyyy');

  const getRequestedPortDate = (): string =>
    getFormattedDate(port?.portingRequests?.[0]?.requestedFirmOrderCommitmentDate);

  const { getFieldProps } = useForm({
    fields: {
      asapPort: { type: 'checkbox', value: port.requestAsapPort },
      phone: { type: 'phone' },
      requestedPortDate: {
        type: 'datePicker',
        minDate: getUTCDateWithPortingTimeString(today.toISOString()),
        value: port?.portingRequests?.[0]?.requestedFirmOrderCommitmentDate
          ? getRequestedPortDate()
          : '',
        validator: ({ value }: ValidatorFieldState<'datePicker'>) => {
          const formattedValue = convertUsDateToIsoDateFormat(value);
          const formattedMinDate = convertUsDateToIsoDateFormat(minDate);

          return formattedValue < formattedMinDate
            ? 'Requested port date cannot be set in the past'
            : '';
        },
      },
      currentPhoneServiceProvider: {
        type: 'text',
        value: port.currentPhoneServiceProvider ?? '',
        required: true,
      },
    },
  });

  const requestPortDate = getFieldProps('requestedPortDate').value;

  useEffect(() => {
    setPortingProviderError('');
  }, [requestPortDate]);

  useEffect(() => {
    const portCopy = cloneDeep(port);
    const date = requestPortDate && convertToDateWithSpecificTime(requestPortDate);
    const dateInUTCZone =
      requestPortDate &&
      formatInTimeZone(date, 'America/New_York', 'yyyy-MM-dd') + 'T00:00:00Z';
    for (const portingRequest of portCopy?.portingRequests ?? []) {
      portingRequest.requestedFirmOrderCommitmentDate = dateInUTCZone;
    }
    setPortingDetails(portCopy);
  }, [requestPortDate]);

  useEffect(() => {
    if (canEditAttachments) {
      setError(validateMediaData(port.customerPhoneBillMedia));
    }
  }, [port.customerPhoneBillMedia]);

  useEffect(() => {
    window.scroll(0, 0);
    return () => {
      clearSubmitPortingDetails();
    };
  }, [port.id]);

  const updatePortProvider = async () => {
    setPortingInfoError('');
    setPortingProviderError('');
    setPortingInfoEdit(true);

    if (!port.provider) {
      port.provider = Provider.PROVIDER_UNSPECIFIED;
    }
    const portCopy = cloneDeep(port);
    portCopy.provider = providerDropdownProps.value as Provider;
    portCopy.currentPhoneServiceProvider = getFieldProps(
      'currentPhoneServiceProvider'
    ).value;
    if (portCopy?.portingRequests?.[0].portingCarrier) {
      portCopy.portingRequests[0].portingCarrier = portCopy.provider;
    }
    setPortingDetails(portCopy);
    await updatePortingData({ portingData: portCopy, portingDataId: portCopy.id });

    setPortingInfoEdit(false);
  };

  type SplitPortingRequestResult = {
    status: PortInCreateByPortingDataIDResponse;
    port?: PortingData;
  };
  const [splitPortingRequestsResults, setSplitPortingRequestsResults] = useState<
    SplitPortingRequestResult[]
  >([]);
  const [splitPortingRequests, setSplitPortingRequests] = useState<PortingData[]>([]);
  const [submitPortingDetails, setSubmitPortingDetails] =
    useState<PortInCreateByPortingDataIDResponse | null>(null);
  const clearSubmitPortingDetails = () => setSubmitPortingDetails(null);
  const [submitPortingLoading, setSubmitPortingLoading] = useState<boolean>(false);

  const handleErrorCodeData = async (portingData: PortingData) => {
    const response = await portingSchemaApi.portInStatusByPortingDataId({
      portingDataId: portingData.id,
    });
    if (
      response.progressStatus === 'VALIDATION_FAILED' &&
      portingData?.portingRequests?.[0]
    ) {
      portingData.portingRequests[0].portingStatus =
        PortStatus.PORT_STATUS_VALIDATION_FAILED;
      setPortingDetails(portingData);
    }
  };

  const submitSplitPortingRequests = async (portingData: PortingData) => {
    const originalPortOrder = cloneDeep(portingData);
    setSubmitPortingLoading(true);

    try {
      try {
        // set the original to status PORT_STATUS_SPLIT_PORT
        for (const portingRequest of originalPortOrder.portingRequests ?? []) {
          portingRequest.portingStatus = PortStatus.PORT_STATUS_SPLIT_PORT;
          portingRequest.portOrderNumber = 'No Port Order Number - Split Port';
        }

        await portingSchemaApi.updatePortingDataById({
          portingData: originalPortOrder,
          portingDataId: originalPortOrder.id,
        });
      } catch {
        alert.error('Failed to update original porting request to split status.');
      }

      const results: SplitPortingRequestResult[] = [];

      for (const portingRequest of splitPortingRequests) {
        delete portingRequest.id;
        delete portingRequest.createdAt;
        delete portingRequest.updatedAt;

        if (!portingRequest.serviceHouseNumber) {
          const streetSplit = portingRequest?.serviceStreet1?.split(' ') ?? [];
          portingRequest.serviceHouseNumber = streetSplit?.[0];
          streetSplit?.shift();
          portingRequest.serviceStreet1 = streetSplit?.join(' ');
        }

        try {
          // create new portingData objects
          const res = await portingSchemaApi.createPortingData({
            portingData: portingRequest,
          });
          const newPortOrderId = res.portingData?.[0].id;

          // submit new portingData objects
          const response = await portingSchemaApi.portInCreateByPortingDataId({
            portingDataId: newPortOrderId,
          });

          const status = response; // submitPortingDetails

          // get the new portingData object after submission
          const newPortingDataResponse =
            await portingSchemaApi.getPortingDataByLocationIds([
              currentLocation.LocationID,
            ]);

          let updatedPort: PortingData | undefined = undefined;
          const newPortingData = newPortingDataResponse.portingData ?? [];
          for (const portingData of newPortingData) {
            if (portingData.id === newPortOrderId) {
              updatedPort = portingData;
            }
          }

          results.push({ status, port: updatedPort });
        } catch (error) {
          if (error?.response?.status === 400) {
            handleErrorCodeData(originalPortOrder);
          }
          const errorMessage = getErrorMessageFromResponseError(error);
          results.push({
            status: {
              hasError: true,
              errors: [
                {
                  description:
                    errorMessage || 'Network request failed. Simply try resubmitting.',
                },
              ],
            },
          });
        }
      }

      setSplitPortingRequestsResults(results);
      setSplitPortingRequests([]);
    } catch {
      alert.error('Could not split and submit porting requests.');
      setSplitPortingRequests([]);
      setSplitPortingRequestsResults([
        {
          status: {
            hasError: true,
            errors: [{ description: 'Network request failed. Simply try resubmitting.' }],
          },
          port: undefined,
        },
      ]);
    } finally {
      setSubmitPortingLoading(false);
    }
  };

  const submitPorting = async (portingData: PortingData) => {
    setSubmitPortingLoading(true);
    const portClone = cloneDeep(portingData);
    try {
      if (!portClone.serviceHouseNumber) {
        const streetSplit = portClone?.serviceStreet1?.split(' ') ?? [];
        portClone.serviceHouseNumber = streetSplit[0];
        streetSplit.shift();
        portClone.serviceStreet1 = streetSplit.join(' ');
      }

      if (isSmsHosting) {
        // Read the agreement content template for feature 0 (porting)
        const agreementContent = await AgreementContentService.ReadByFeature(
          authorizedFetch(),
          // @ts-expect-error: Need to ignore this error. It is a bug in the schema-gen-ts code.
          // The schema expects a number but there is an incorrect check in the schema-gen-ts
          // code where it will treat number 0 as empty and throw an error.
          // Error here --> https://github.com/weave-lab/schema-gen-ts/blob/4f360690cd41e1b20f7d60d6c9374bc5abcce71f/src/fetch.pb.ts#L249
          { feature: '0' }
        );

        // Create a signed agreement record
        const newAgreement = await InsysAgreementsAPI.Create(authorizedFetch(), {
          agreementContentId: agreementContent.id,
          firstName: portClone.authorizedUserFirstName,
          lastName: portClone.authorizedUserLastName,
          userEmail: username,
          userId: userId,
        });

        // Link the agreement record to our port order
        portClone.agreementId = newAgreement.id;
      }

      const res = await portingSchemaApi.updatePortingDataById({
        portingData: portClone,
        portingDataId: portClone.id,
      });
      const updatedPort = res.portingData;

      let response;
      if (getPortOrderType(port) === 'SMS Hosting') {
        const smsResponse = await PortingDataService.PortInSMSCreateByPortingDataID(
          authorizedFetch(),
          {
            portingDataId: updatedPort?.id,
          }
        );
        response = {
          hasError: smsResponse.order?.orderStatus === 'SMS_PORT_ORDER_STATUS_FAILED',
          errors:
            smsResponse.order?.orderStatus === 'SMS_PORT_ORDER_STATUS_FAILED'
              ? [{ description: 'SMS hosting submit failed.' }]
              : [],
          orderId: smsResponse.order?.orderId,
        };
      } else {
        response = await portingSchemaApi.portInCreateByPortingDataId({
          portingDataId: updatedPort?.id,
        });
      }

      setSubmitPortingDetails(response);
    } catch (error) {
      if (error?.response?.status === 400) {
        handleErrorCodeData(portClone);
      }
      const errorMessage = getErrorMessageFromResponseError(error);
      setSubmitPortingDetails({
        hasError: true,
        errors: [
          {
            description:
              errorMessage || 'Network request failed. Simply try resubmitting.',
          },
        ],
      });
    } finally {
      setSubmitPortingLoading(false);
    }
  };

  const checkPortability = async (portOrder: PortingData) => {
    setSubmitPortingLoading(true);
    try {
      const portClone = cloneDeep(portOrder);
      const LNPNumbers: string[] = [];
      for (const req of portClone?.portingRequests ?? []) {
        if (req.phoneNumber) {
          LNPNumbers.push(req.phoneNumber);
        }
      }
      const provider = portClone.provider;
      const LNPCheckResponse = await portingSchemaApi.checkPortability({
        phoneNumbers: LNPNumbers,
        provider,
      });

      if (LNPCheckResponse.errors) {
        for (const error of LNPCheckResponse.errors) {
          alert.error(
            `Check portability failed for ${error.phoneNumbers}. Not Portable Reason ${error.description}`
          );
          setSplitPortingRequests([]);
        }
      }

      if (LNPCheckResponse.portabilityErrors) {
        for (const portabilityError of LNPCheckResponse.portabilityErrors) {
          alert.error(
            `Check portability failed for ${portabilityError.phoneNumbers}. Not Portable Reason ${portabilityError.description}`
          );
        }
        setSplitPortingRequests([]);
      }

      if (
        !LNPCheckResponse.portableNumbers ||
        LNPCheckResponse.portableNumbers?.length === 0
      ) {
        const numbersDetail =
          'Number(s): ' +
          (LNPCheckResponse.unsupportedRateCenters ?? [])
            .flatMap((a_rate_center) => a_rate_center.phoneNumbers)
            .join(', ');

        alert.error(
          `Numbers not eligible for porting. Port type: ${LNPCheckResponse.portType} , ${numbersDetail}`
        );
        setSplitPortingRequests([]);
      } else {
        const portingDataObjects: PortingData[] = getPortingDataObjectList(
          portClone,
          LNPCheckResponse
        );
        if (portingDataObjects.length === 1) {
          submitPorting(portOrder);
        } else {
          // This would just save the portingData objects in the redux store and also the the amount of objects (length of the array)
          // it would then just be passed back down to be used in this component through drill propping.
          // It might also be used in the submitting of port orders
          // This is what was saved in the redux store:
          //   submitPortingLoading: false,
          // splitPortingRequest: action.payload.length,
          // splitPortingRequests: action.payload,
          // yield put(checkPortabilitySuccess(portingDataObjects));
          // setSplitPortingRequest(portingDataObjects.length);
          setSplitPortingRequests(portingDataObjects);
        }
      }
    } catch {
      alert.error('Could not submit this port order. Failed to check portability.');
      setSplitPortingRequests([]);
    } finally {
      setSubmitPortingLoading(false);
    }
  };

  const resetPortabilityCheck = () => {
    setSplitPortingRequests([]);
    setSplitPortingRequestsResults([]);
  };

  const submit = () => {
    const err = validate();
    if (!err) {
      setError('');
      if (isSmsHosting) {
        submitPorting(port);
      } else {
        checkPortability(port);
      }
    } else {
      setError(err);
    }
  };

  const validate = (): string => {
    if (
      !port?.portingRequests?.[0]?.requestedFirmOrderCommitmentDate &&
      !port?.requestAsapPort
    ) {
      setMissingReqestedPort(true);
    }
    if (!port?.provider) {
      setMissingPortingCompany(true);
    }
    if (
      !(
        port?.companyName &&
        port?.authorizedUserFirstName &&
        (isSmsHosting || port?.phoneServiceAccountNumber) &&
        port?.serviceCity &&
        port?.serviceStreet1 &&
        port?.serviceZip &&
        port?.serviceState &&
        port?.provider
      )
    ) {
      return 'Oops! We were unable to submit your port. Please make sure fields highlighted field are not empty and resubmit.';
    }

    return validateMediaData(port.customerPhoneBillMedia);
  };

  const validateMediaData = (mediaData?: MediaData[]): string => {
    if (port?.serviceCountry === CountryCodes.USA.Code) {
      return '';
    }
    if (
      (!mediaData ||
        mediaData.length === 0 ||
        mediaData?.findIndex(
          (md: MediaData) =>
            md.mediaType === MediaType.phoneBill ||
            md.mediaType === PortingMediaType.MEDIA_TYPE_PHONE_BILL
        ) === -1) &&
      !port.reasonBillNotAvailable
    ) {
      return 'Either phone bill should be uploaded or reason of bill not available should be provided.';
    }

    return '';
  };

  const handleSendToSalesforce = async () => {
    if (!port.id) return;

    createSFPortOrder({ batchId: port.id });
  };

  const handleDrawerToggle = (
    drawer: string,
    toggleState: boolean,
    disableToggle = false
  ) => {
    switch (drawer) {
      case 'porting':
        setPortingInfoEdit(!toggleState);
        break;
      case 'phone':
        setPhoneNumbersEdit(!toggleState);
        break;
      case 'office':
        setOfficeInfoEdit(!toggleState);
        break;
      case 'attachments':
        if (!disableToggle) setCanEditAttachments(!toggleState);
        else {
          alert.warning('Please complete the form and save.');
        }
        break;
      default:
        break;
    }
  };

  const portingRequest = port.portingRequests?.[0];
  const isSmsHosting = getPortOrderType(port) === 'SMS Hosting';
  const filterDidsNumber = port?.portingRequests?.filter((portingNumber) =>
    isVoiceSmsDIDNumber(portingNumber.numberType)
  );
  const filterFaxDidsNumber = port?.portingRequests?.filter((portingNumber) =>
    isFaxDIDNumber(portingNumber.numberType)
  );
  const chunkedPhoneNumbers: PortingRequest[][] = chunk(filterDidsNumber, 9);
  const chunkedFaxNumbers: PortingRequest[][] = chunk(filterFaxDidsNumber, 9);
  const invalidCancellationPortStatuses = [
    PortStatus.PORT_STATUS_CANCELLED,
    PortStatus.PORT_STATUS_CANCEL_PENDING,
    PortStatus.PORT_STATUS_CANCEL_SUBMITTED,
    PortStatus.PORT_STATUS_REQUESTED_CANCEL,
    PortStatus.PORT_STATUS_COMPLETED,
    PortStatus.PORT_STATUS_SPLIT_PORT,
    PortStatus.PORT_STATUS_ACTIVATION_IN_PROGRESS,
    PortStatus.PORT_STATUS_VALIDATE_TFNS,
  ];

  const invalidSendToSaleForceStatus = [
    PortStatus.PORT_STATUS_COMPLETED,
    PortStatus.PORT_STATUS_SPLIT_PORT,
  ];

  const validSUPRequestPortStatuses = [PortStatus.PORT_STATUS_FOC];

  const showPortOrderCancelButton = (): boolean => {
    if (portingRequest?.portingStatus) {
      const portStatus = portingRequest?.portingStatus;

      if (invalidCancellationPortStatuses.includes(portStatus)) {
        return false;
      }
    }
    return true;
  };

  const showSendToSaleForceButton = (): boolean => {
    if (portingRequest?.portingStatus) {
      const portStatus = portingRequest?.portingStatus;

      if (invalidSendToSaleForceStatus.includes(portStatus)) {
        return false;
      }
    }
    return true;
  };

  function getBusinessDatesCount(startDate, endDate) {
    /*this function assign number to each date based on the day its occuring for
  eg sun - 0 to sat- 7 so the date we are passing is also considered in the count for eg 14-dec to 19 dec it will return 4 as the count  */
    let count = 0;
    const curDate = new Date(startDate.getTime());
    while (curDate <= endDate) {
      const dayOfWeek = curDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
      curDate.setDate(curDate.getDate() + 1);
    }
    return count;
  }

  const canCancelORRaiseSUPRequest = (status: string): boolean => {
    if (!port.portingRequests?.[0]?.firmOrderCommitmentDate) {
      return true;
    }

    const startDate = new Date();
    const endDate = new Date(port.portingRequests?.[0]?.firmOrderCommitmentDate!);

    const numOfDates = getBusinessDatesCount(startDate, endDate);

    if (numOfDates <= 4) {
      /*we are checking here with 4 beacuse the starting date is also included in the function */
      alert.error(
        `Cannot ${status} as FOC is within 3 business days. Please contact Porting team`
      );
      return false;
    }
    return true;
  };

  const handleCancelClick = () => {
    if (canCancelORRaiseSUPRequest('cancel')) {
      openCancelModal();
    }
  };

  const goToPortingHub = () => {
    history.replace(
      injectParams(ONBOARDING.portingV2, { id: currentLocation?.LocationID })
    );
  };

  const goToPortOrderList = (portingDataId?: string) => {
    if (!portingDataId) return;
    history.replace(
      injectParams(ONBOARDING.portingV2Detail, {
        id: currentLocation?.LocationID,
        portingDataId,
      })
    );
  };

  const handleDelete = () => {
    if (!port.id) return;

    deletePortingData(port.id, goToPortingHub);
  };

  const cancelPhoneChanges = () => {
    setNewPhoneNumbersList(existingNumberValues);
    setNewFaxNumbersList(existingFaxValue);
    setPhoneNumbersEdit(false);
    closePhoneNumbersEditDiscardModal();
  };

  const showSupRequestButton =
    portingRequest?.portingStatus &&
    validSUPRequestPortStatuses.includes(portingRequest?.portingStatus);

  const cancelPort = () => {
    if (getPortOrderType(port) === 'SMS Hosting') {
      cancelSMSPortOrderMutation({
        portingDataId: port.id,
        userEmail: username,
      });
    } else {
      cancelPortOrderMutation({
        portingDataId: port.id,
        userEmail: username,
      });
    }
  };

  const handleClonePortingData = async () => {
    if (!port.id) return;

    clonePortOrder({ portingDataId: port.id, userId });
  };

  const handleUploadLoaClick = (orderId: string) => {
    downloadLoa(orderId);
    const url = `https://dashboard.bandwidth.com/portal/report/#orderdetail:%7B"a":5001811,"t":"PORT_IN","o":"${orderId}"%7D`;
    window.open(url, '_blank');
  };
  const submitButtonDisplay =
    portingInfoEdit ||
    officeInfoEdit ||
    phoneNumbersEdit ||
    !!portingValidationData?.length;

  const getPhoneNumberList = (
    chunkedPhoneNumbers: PortingRequest[][],
    numberTypeList: NumberType[],
    newPhoneNumberField: PortingRequest[]
  ) => {
    return (
      <div>
        {chunkedPhoneNumbers.map((phoneNumbers, ind) => (
          <div key={ind} className={portingCardStyles.phoneCont}>
            {phoneNumbers
              .filter(
                (portingRequest) =>
                  !!portingRequest.numberType &&
                  numberTypeList.includes(portingRequest.numberType)
              )
              .map(
                (request, index) =>
                  !phoneNumbersEdit && (
                    <Text
                      key={index}
                      css={css`
                        color: ${theme.colors.gray500};
                        margin-left: 10px;
                      `}
                    >
                      {request.phoneNumber}
                    </Text>
                  )
              )}
          </div>
        ))}
        {phoneNumbersEdit && (
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;

              margin-bottom: ${theme.spacing(1)};
              margin-left: ${theme.spacing(4)};
            `}
          >
            {newPhoneNumberField?.map((existingNumberField: PortingRequest, index) => (
              <div
                key={index}
                css={css`
                  display: flex;
                  margin-bottom: ${theme.spacing(2)};
                `}
              >
                <PhoneField
                  {...getFieldProps('phone')}
                  value={existingNumberField?.phoneNumber || ''}
                  onChange={(value) =>
                    handleNumberChange(value, index, numberTypeList[0])
                  }
                  label="Add a phone number"
                />
                <IconButton
                  label="Remove Phone Number"
                  hoverLabelPlacement="top"
                  showLabelOnHover
                  css={css`
                    height: 24px;
                    width: 24px;
                    svg {
                      fill: #ff6b59;
                    }
                  `}
                >
                  <TrashIcon
                    color="error"
                    onClick={() => handleNumberDeletion(index, numberTypeList[0])}
                  />
                </IconButton>
                {newPhoneNumberField?.length - 1 === index && (
                  <IconButton
                    label="Add Number"
                    onClick={() => handleNewNumberAddition(numberTypeList[0])}
                    css={iconButtonSvgPrimary}
                    disabled={(existingNumberField?.phoneNumber?.length ?? 0) <= 3}
                  >
                    <PlusIcon />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
        )}
        {phoneNumbersEdit && !newPhoneNumberField.length && (
          <div
            style={{
              marginTop: `${theme.spacing(-2)}`,
              marginLeft: `${theme.spacing(6)}`,
            }}
          >
            <IconButton
              label="Add Number"
              onClick={() => handleNewNumberAddition(numberTypeList[0])}
              css={iconButtonSvgPrimary}
            >
              <PlusIcon />
            </IconButton>
          </div>
        )}
      </div>
    );
  };

  const voiceSmsPhoneNumbersList = useMemo(
    () =>
      getPhoneNumberList(
        chunkedPhoneNumbers,
        isSmsHosting
          ? [NumberType.NUMBER_TYPE_SMS]
          : [NumberType.NUMBER_TYPE_PHONE, NumberType.NUMBER_TYPE_SMS],
        newPhoneNumbersList
      ),
    [chunkedPhoneNumbers, phoneNumbersEdit, newPhoneNumbersList, isSmsHosting]
  );

  const faxPhoneNumbersList = useMemo(
    () =>
      getPhoneNumberList(
        chunkedFaxNumbers,
        [NumberType.NUMBER_TYPE_FAX],
        newFaxNumbersList
      ),
    [chunkedFaxNumbers, phoneNumbersEdit, newFaxNumbersList]
  );

  const appendPortingRequests = (
    newNumberField: PortingRequest[],
    portCopy,
    portingRequests: PortingRequest[]
  ) =>
    newNumberField.map((numberField) =>
      numberField === portCopy.billing_phone_number
        ? [...portingRequests].concat(portCopy.portingRequests)
        : [...portingRequests].concat(numberField)
    );

  const handlePhoneNumberUpdate = async () => {
    const portCopy = cloneDeep(port);
    const portingRequests: PortingRequest[] = [];
    const newNumberdata = appendPortingRequests(
      newPhoneNumbersList,
      portCopy,
      portingRequests
    );
    const newFaxData = appendPortingRequests(
      newFaxNumbersList,
      portCopy,
      portingRequests
    );

    if (isSmsHosting) {
      const smsNumbers = newPhoneNumbersList
        .map((r) => r.phoneNumber ?? '')
        .filter(Boolean)
        .join(',');

      if (!validatePhoneNumbersRegex(smsNumbers)) {
        setPhoneNumberFormatError('Please enter valid SMS phone numbers');
        return;
      }
    }

    if (port.portType !== PortingType.PORTING_TYPE_INTERNAL) {
      try {
        const existingNumberSet = new Set([
          ...existingNumberValues.map((r) => r.phoneNumber ?? ''),
          ...existingFaxValue.map((r) => r.phoneNumber ?? ''),
        ]);
        const newNumbersWithTypes = [
          ...newPhoneNumbersList.map((r) => ({
            phoneNumber: r.phoneNumber ?? '',
            numberType: r.numberType ?? NumberType.NUMBER_TYPE_PHONE,
          })),
          ...newFaxNumbersList.map((r) => ({
            phoneNumber: r.phoneNumber ?? '',
            numberType: NumberType.NUMBER_TYPE_FAX,
          })),
        ].filter(
          ({ phoneNumber }) =>
            phoneNumber.length >= MIN_PHONE_NUMBER_LENGTH &&
            !existingNumberSet.has(phoneNumber)
        );

        const numbersToValidate = newNumbersWithTypes.map(
          ({ phoneNumber }) => phoneNumber
        );

        if (numbersToValidate.length) {
          const requestedNumberTypesByNumber =
            buildRequestedNumberTypesMap(newNumbersWithTypes);

          const response = await queryClient.fetchQuery(
            PortingQueries.getCheckPhoneNumbersExistQueryOptions(numbersToValidate)
          );
          const alreadyPorted = getConflictingExistingNumbers(
            response?.phoneNumberExistenceResults ?? [],
            requestedNumberTypesByNumber
          );
          if (alreadyPorted.length > 0) {
            setExistingPortedNumbers(alreadyPorted);
            return;
          }
        }
      } catch (error) {
        console.error('Error validating phone numbers', error);
      }
    }

    if (isSmsHosting) {
      try {
        const newSmsNumbers = newPhoneNumbersList
          .map((r) => r.phoneNumber ?? '')
          .filter((n) => n.length >= MIN_PHONE_NUMBER_LENGTH);

        const { results } = await checkSmsHostabilityBatch({
          phoneNumbers: newSmsNumbers,
        });
        const nonHostable = results
          .filter((r) => !r.validationPassed)
          .map((r) => r.phoneNumber)
          .filter(Boolean);

        if (nonHostable.length > 0) {
          setNonHostableNumbersError(
            `These numbers are not eligible for SMS hosting: ${nonHostable.join(', ')}`
          );
          return;
        }

        // Set smsPortEligible flag to true for successfully validated numbers
        newPhoneNumbersList.forEach((r) => {
          r.smsPortEligible = true;
        });
      } catch (error) {
        console.error('Error checking SMS hostability', error);
        return;
      }
    }

    try {
      const newPortingRequests = [...newNumberdata, ...newFaxData].flat();
      const resp = await portingSchemaApi.processPortingRequestsByPhoneNumbers({
        portingRequests: newPortingRequests,
      });

      portCopy.portingRequests = resp.portingRequests;
      invalidatePortingData(portCopy.id);
      setPortingDetails(portCopy);
      setPhoneNumbersEdit((prevState) => !prevState);
      setDuplicateError('');
    } catch (error) {
      setDuplicateError(getParsedErrorMessage(error));
    }
  };

  const togglePhoneNumberEdit = () => {
    if (phoneNumbersEdit && hasUnsavedPhoneNumberChanges) {
      openPhoneNumbersEditDiscardModal();
      return;
    }
    handleDrawerToggle('phone', phoneNumbersEdit);
  };

  const updatePortDetailsByFoc = (port: PortingData) => {
    setPortingDetails(port);
  };

  const handleSUPRequestClick = () => {
    if (canCancelORRaiseSUPRequest('request SUP')) {
      openSUPModal();
    }
  };

  const showLoading =
    downloadLOALoading ||
    submitPortingLoading ||
    isClonePortMutating ||
    isSFPortOrderMutating ||
    isUpdatingPortingData;

  return (
    <div>
      <div className={portingCardStyles.displayStyle}>
        <Text
          className={portingCardStyles.portingHubText}
          css={css`
            margin-bottom: ${theme.spacing(0)};
          `}
        >
          View, edit, or submit information for this port order below
        </Text>
        <div className={portingCardStyles.requestContainerButtons}>
          <div className={portingCardStyles.supRequest}>
            {showSupRequestButton && (
              <PrimaryButton size="large" color="blue" onClick={handleSUPRequestClick}>
                SUP Request
              </PrimaryButton>
            )}
          </div>
          <div className={portingCardStyles.cancellationRequest}>
            {isDebugModeEnabled && <DeletePortingDataButton onConfirm={handleDelete} />}
            {showPortOrderCancelButton() && (
              <PrimaryButton size="large" destructive onClick={handleCancelClick}>
                Cancellation Request
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
      <div className={portingCardStyles.errorMessage}>{error}</div>
      <div className={portingCardStyles.container}>
        <div className={portingCardStyles.backgroundStyle}>
          <div className={portingCardStyles.flexCenter}>
            <div
              css={css`
                margin-right: 15px;
              `}
            >
              <div
                css={css`
                  padding: ${theme.spacing(1.5)};
                  border: 1px solid ${getPortingStatusColor(portingRequest)};
                  background-color: ${getPortingStatusColor(portingRequest)};
                  border-radius: 50%;
                `}
              />
            </div>
            <div
              css={css`
                width: 100%;
              `}
            >
              <div className={portingCardStyles.flexContainer}>
                <div>{getStatusForPorting(port)}</div>
                {!isSmsHosting && (
                  <div>
                    Requested Port Date:{' '}
                    {portingRequest?.requestedFirmOrderCommitmentDate &&
                      getRequestedPortDate()}
                  </div>
                )}
              </div>

              <div className={portingCardStyles.portOrderType}>
                <div>Port Order # {portingRequest?.portOrderNumber}</div>
                <div>{getPortOrderType(port)}</div>
              </div>

              <div
                css={css`
                  text-align: right;
                  margin-top: 5px;
                `}
              >
                <Text>
                  Created Date:{' '}
                  {portingRequest?.createdAt &&
                    format(new Date(portingRequest?.createdAt), 'MM/dd/yy')}
                </Text>
              </div>
            </div>
          </div>
        </div>
        <div
          css={css`
            padding: 25px;
          `}
        >
          <div className={portingCardStyles.portingInfoContainer}>
            <div className={portingCardStyles.title}>Porting Information</div>
            {!portingRequest?.portOrderNumber && (
              <div>
                <EditIcon
                  css={css`
                    color: ${!portingInfoEdit && theme.colors.weaveBlue};
                  `}
                  onClick={() => handleDrawerToggle('porting', portingInfoEdit)}
                />
              </div>
            )}
          </div>
          {portingInformationError?.map((data) => (
            <Text color="error" key={data.id}>
              {data.reason}
            </Text>
          ))}

          <div className={portingCardStyles.requestedPortDate}>
            <Text
              css={css`
                ${portingInfoEdit ? 'margin-bottom: 12px;' : 'margin-bottom: 0px;'};
              `}
            >
              Porting Data Id
            </Text>
            <Text
              css={css`
                color: ${theme.colors.gray500};
                ${portingInfoEdit ? 'margin-bottom: 12px;' : 'margin-bottom: 0px;'};
              `}
            >
              {port.id}
            </Text>

            <Text
              css={css`
                ${portingInfoEdit ? 'margin-bottom: 12px;' : 'margin-bottom: 0px;'};
              `}
            >
              Porting Order Name
            </Text>
            <Text
              css={css`
                color: ${theme.colors.gray500};
                ${portingInfoEdit ? 'margin-bottom: 12px;' : 'margin-bottom: 0px;'};
              `}
            >
              {port.sfPortOrderName}
            </Text>

            <div>Request Client</div>
            <div
              css={css`
                color: ${theme.colors.gray500};
              `}
            >
              {port.requestClient}
            </div>

            {!!port.requestAsapPort && (
              <>
                <div>ASAP Port</div>
                <FormRow
                  css={css`
                    margin-bottom: 0;
                  `}
                >
                  <CheckboxField
                    {...getFieldProps('asapPort')}
                    name="asapPort"
                    label={
                      <Text
                        css={css`
                          margin-bottom: 0;
                          color: ${theme.colors.gray500};
                        `}
                      >
                        Request ASAP Port
                      </Text>
                    }
                    disabled
                  />
                </FormRow>
              </>
            )}

            {!isSmsHosting && (
              <>
                <div
                  css={css`
                    color: ${missingRequestedPort && !portingInfoEdit
                      ? theme.colors.errorPressed
                      : null};
                  `}
                >
                  Requested Port Date
                </div>
                <div
                  css={css`
                    color: ${theme.colors.gray500};
                  `}
                >
                  {portingInfoEdit && (
                    <FormRow>
                      <DatePickerField
                        {...getFieldProps('requestedPortDate')}
                        name="requestedPortDate"
                        label="Port Date"
                      />
                    </FormRow>
                  )}
                  {portingRequest?.requestedFirmOrderCommitmentDate &&
                    !portingInfoEdit &&
                    getRequestedPortDate()}
                </div>

                {port.portingRequests?.[0]?.firmOrderCommitmentDate && (
                  <>
                    <Text
                      css={css`
                        margin-bottom: ${theme.spacing(0)};
                      `}
                    >
                      Actual FOC Date
                    </Text>
                    <Text
                      css={css`
                        color: ${theme.colors.gray500};
                        margin-bottom: ${theme.spacing(0)};
                      `}
                    >
                      {port.portingRequests?.[0]?.firmOrderCommitmentDate &&
                        !portingInfoEdit &&
                        getFormattedDate(
                          port.portingRequests?.[0]?.firmOrderCommitmentDate
                        )}
                    </Text>
                  </>
                )}

                <div
                  css={css`
                    color: ${missingPortingCompany && !portingInfoEdit
                      ? theme.colors.errorPressed
                      : null};
                  `}
                >
                  Porting Company
                </div>
                {portingInfoEdit ? (
                  <DropdownField
                    {...providerDropdownProps}
                    label="Provider"
                    name="provider"
                    css={css`
                      width: 200px;
                    `}
                  >
                    <DropdownField.Option value={Provider.PROVIDER_UNSPECIFIED}>
                      Unspecified
                    </DropdownField.Option>
                    <DropdownField.Option value={Provider.PROVIDER_BANDWIDTH}>
                      Bandwidth
                    </DropdownField.Option>
                    <DropdownField.Option value={Provider.PROVIDER_TELNYX}>
                      Telnyx
                    </DropdownField.Option>
                  </DropdownField>
                ) : (
                  <div
                    css={css`
                      color: ${theme.colors.gray500};
                    `}
                  >
                    {port.provider}
                  </div>
                )}
              </>
            )}

            <>
              <Text css={{ margin: 0 }}>Current Phone Provider</Text>
              {portingInfoEdit ? (
                <div css={{ marginTop: theme.spacing(1), width: 'fit-content' }}>
                  <TextField
                    {...getFieldProps('currentPhoneServiceProvider')}
                    name="currentPhoneServiceProvider"
                    label="Current Phone Provider"
                  />
                </div>
              ) : (
                <Text
                  css={css`
                    color: ${theme.colors.gray500};
                    margin: 0px;
                  `}
                >
                  {port?.currentPhoneServiceProvider || '-'}
                </Text>
              )}
            </>

            <div>Porting Order URL</div>
            <div
              css={css`
                color: ${theme.colors.gray500};
              `}
            >
              <a href={portOrderUrl} target="_blank" style={{ color: '#37CFF1' }}>
                {portOrderUrl}
              </a>
            </div>
            <>
              <Text
                css={css`
                  margin-bottom: ${portingInfoEdit ? '12px' : '0px'};
                `}
              >
                Port Type
              </Text>
              <Text
                css={css`
                  color: ${theme.colors.gray500};
                  margin-bottom: ${portingInfoEdit ? '12px' : '0px'};
                `}
              >
                {port?.portType && getTypeOfPortingRequest()}
              </Text>
            </>

            {portingInfoEdit && (
              <div
                css={css`
                  width: 200px;
                  margin: 10px auto;
                `}
              >
                <PrimaryButton
                  className={portingCardStyles.savePortCont}
                  color="blue"
                  size="large"
                  onClick={() => {
                    if (
                      !!portingRequest?.requestedFirmOrderCommitmentDate &&
                      (isHoliday(portingRequest?.requestedFirmOrderCommitmentDate) ||
                        isWeekend(portingRequest?.requestedFirmOrderCommitmentDate))
                    ) {
                      setPortingInfoError('Date cannot be a weekend or a major holiday.');
                    } else if (!getFieldProps('currentPhoneServiceProvider').value) {
                      setPortingInfoError('Current Phone Provider cannot be empty.');
                    } else {
                      updatePortProvider();
                    }
                  }}
                >
                  Save
                </PrimaryButton>
                {portingInfoError && (
                  <div
                    css={css`
                      margin-top: 10px;
                      color: red;
                    `}
                  >
                    {portingInfoError}
                  </div>
                )}
                {!!portingProviderError && (
                  <Text
                    css={css`
                      margin-top: 10px;
                    `}
                    color="error"
                  >
                    {portingProviderError}
                  </Text>
                )}
              </div>
            )}
          </div>

          <div className={portingCardStyles.portingInfoContainer}>
            <div
              css={css`
                font-weight: bold;
                font-size: 16px;
              `}
            >
              Phone Numbers
            </div>
            {!portingRequest?.portOrderNumber && (
              <div>
                <EditIcon
                  css={css`
                    color: ${!phoneNumbersEdit && theme.colors.weaveBlue};
                  `}
                  onClick={togglePhoneNumberEdit}
                />
              </div>
            )}
          </div>
          {phoneNumberError?.map((data) => (
            <Text color="error" key={data.id}>
              {data.reason}
            </Text>
          ))}
          <div className={portingCardStyles.dids}>
            <div
              css={css`
                display: flex;
                margin-top: 30px;
              `}
            >
              <div
                css={css`
                  margin-bottom: ${theme.spacing(2.5)};
                  margin-right: ${theme.spacing(phoneNumbersEdit ? 3.2 : 9.2)};
                `}
              >
                {isSmsHosting ? 'SMS Numbers' : 'DID(s)'}
              </div>
              <div>{voiceSmsPhoneNumbersList}</div>
            </div>

            {!isSmsHosting && (
              <div
                css={css`
                  display: flex;
                  margin-top: ${theme.spacing(2)};
                `}
              >
                <div
                  css={css`
                    flex: 0 0 auto;
                    margin-bottom: 10px;
                    ${phoneNumbersEdit ? 'margin-right: 0px;' : 'margin-right: 47px;'};
                  `}
                >
                  Fax DID(s)
                </div>
                <div>{faxPhoneNumbersList}</div>
              </div>
            )}
            <Text
              css={css`
                text-align: center;
              `}
              color="error"
            >
              {phoneNumberFormatError || nonHostableNumbersError || duplicateError}
            </Text>
            {duplicateNumberError && (
              <Text color="error">Please remove the duplicate Number </Text>
            )}
            {existingPortedNumbers.length > 0 && (
              <Text color="error">
                These numbers are already ported: {existingPortedNumbers.join(', ')}
              </Text>
            )}
            {phoneNumbersEdit && (
              <div className={portingCardStyles.saveOfficeCont}>
                <PrimaryButton
                  className={portingCardStyles.saveOffice}
                  color="blue"
                  size="large"
                  onClick={handlePhoneNumberUpdate}
                  disabled={isPhoneNumbersSaveDisabled}
                >
                  Save
                </PrimaryButton>
              </div>
            )}
          </div>

          <PortingCardOfficeInfo
            currentPortOrder={port}
            officeInfoEdit={officeInfoEdit}
            setOfficeInfoEdit={setOfficeInfoEdit}
            handleDrawerToggle={handleDrawerToggle}
            updatePortingData={updatePortingData}
            officeInformationError={officeInformationError}
            isSmsHosting={isSmsHosting}
          />

          <PortingCardAttachments
            port={port}
            canEditAttachments={canEditAttachments}
            setCanEditAttachments={setCanEditAttachments}
            handleDrawerToggle={handleDrawerToggle}
            updatePortingData={updatePortingData}
            documentError={documentError}
            showBillAttachment={!isSmsHosting}
          />

          {!portingRequest?.portOrderNumber && (
            <div className={portingCardStyles.nextStepContainer}>
              <div className={portingCardStyles.title}>Next Steps</div>
            </div>
          )}

          {!portingRequest?.portOrderNumber && (
            <div className={portingCardStyles.whatisnext}>
              <div>1. Review port information</div>
              <div>2. Submit port request</div>
            </div>
          )}

          <div className={portingCardStyles.errorText}>{error}</div>
          {!!portingValidationData?.length && (
            <Text color="error"> Please fix the above validation errors</Text>
          )}
          <div className={portingCardStyles.buttonContainers}>
            {!portingRequest?.portOrderNumber && (
              <>
                <div className={portingCardStyles.portingCardButtonStyleContainer}>
                  <div className={portingCardStyles.portingCardButtonStyle}>
                    <PrimaryButton
                      className={portingCardStyles.submitButtonStyle}
                      disabled={submitButtonDisplay}
                      color="blue"
                      size="large"
                      onClick={submit}
                      css={css`
                        width: 250px;
                      `}
                    >
                      Submit
                    </PrimaryButton>
                  </div>
                </div>
              </>
            )}
            {showSendToSaleForceButton() && (
              <div className={portingCardStyles.portingCardButtonStyleContainer}>
                <div className={portingCardStyles.portingCardButtonStyle}>
                  <PrimaryButton
                    className={portingCardStyles.submitButtonStyle}
                    disabled={portingInfoEdit || officeInfoEdit || phoneNumbersEdit}
                    color="blue"
                    size="large"
                    onClick={handleSendToSalesforce}
                  >
                    Send to Salesforce
                  </PrimaryButton>
                </div>
              </div>
            )}
            <div className={portingCardStyles.buttonContainersDownload}>
              <div
                className={portingCardStyles.downloadLoaButton}
                onClick={handleClonePortingData}
              >
                Clone
              </div>
            </div>
          </div>
          {!!port.id && (
            <PortAuditTable
              locationId={currentLocation?.LocationID}
              portingDataId={port.id}
            />
          )}
        </div>
      </div>

      <MessageDialogue
        header={messagePortModal.header}
        body={messagePortModal.body}
        actionLabel={messagePortModal.actionLabel}
        messageModalProps={messageModalProps}
        closeMessageModal={messagePortModal.onClose}
        onActionLabelClick={messagePortModal.onActionLabelClick}
      />

      <ContentLoader
        show={showLoading}
        message={
          isClonePortMutating
            ? 'Cloning...'
            : downloadLOALoading
            ? 'Downloading...'
            : isUpdatingPortingData
            ? 'Updating...'
            : 'Submitting...'
        }
      />

      <Modal
        onClose={() => resetPortabilityCheck()}
        css={css`
          padding: ${theme.spacing(2)};
        `}
        show={splitPortingRequests.length > 1}
        className={portingCardStyles.divWidth}
      >
        <ContentLoader show={showLoading} />
        <div>
          <div className={portingCardStyles.lnpText}>
            Due to the complexities of porting, this port request needs to be split.
          </div>

          <div className={portingCardStyles.lnpText}>
            It will be split into {splitPortingRequests.length} porting requests.
          </div>
          <div className={portingCardStyles.loading}>
            <SecondaryButton
              className={portingCardStyles.lnpButton}
              color="gray"
              onClick={() => resetPortabilityCheck()}
            >
              Cancel
            </SecondaryButton>

            <PrimaryButton
              className={portingCardStyles.lnpButton}
              color="blue"
              onClick={() => submitSplitPortingRequests(port)}
            >
              Split & Submit All
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        onClose={() => {
          resetPortabilityCheck();
          onBack();
        }}
        css={css`
          padding: ${theme.spacing(2)};
        `}
        show={splitPortingRequestsResults.length > 0}
        className={portingCardStyles.divWidth}
      >
        <div>
          <div className={portingCardStyles.portingResultsTitle}>Porting Results</div>
          {splitPortingRequestsResults.map((result, index) => {
            if (result?.status?.hasError) {
              return (
                <div
                  className={`${portingCardStyles.splitError} ${
                    index % 2 === 1 ? portingCardStyles.gray : portingCardStyles.white
                  }`}
                  key={index}
                >
                  <div
                    css={css`
                      margin-right: 20px;
                      color: red;
                    `}
                  >
                    Failed
                  </div>
                  <div
                    className={portingCardStyles.uploadLoaButtonSplitFail}
                    onClick={() => {
                      onBack();
                      resetPortabilityCheck();
                      setPortingDetails(result?.port);
                    }}
                  >
                    View Port Order
                  </div>
                  <ul>
                    {result?.status?.errors?.map((error, index) => (
                      <li key={index}>{error.description}</li>
                    ))}
                  </ul>
                </div>
              );
            } else {
              return (
                <div
                  className={`${portingCardStyles.splitSuccess} ${
                    index % 2 === 1 ? portingCardStyles.gray : portingCardStyles.white
                  }`}
                >
                  <div
                    css={css`
                      margin-right: 20px;
                    `}
                  >
                    Success
                  </div>
                  <div
                    className={portingCardStyles.uploadLoaButtonSplit}
                    onClick={() => {
                      onBack();
                      resetPortabilityCheck();
                      setPortingDetails(result?.port);
                    }}
                  >
                    View Port Order
                  </div>

                  {port.provider !== Provider.PROVIDER_TELNYX && (
                    <div
                      className={portingCardStyles.uploadLoaButtonSplit}
                      onClick={() => handleUploadLoaClick(result?.status?.orderId ?? '')}
                    >
                      Upload LOA
                    </div>
                  )}
                </div>
              );
            }
          })}

          <PrimaryButton
            className={portingCardStyles.gotItButton}
            color="blue"
            size="large"
            onClick={() => {
              resetPortabilityCheck();
              onBack();
            }}
          >
            Got it
          </PrimaryButton>
        </div>
      </Modal>

      <Modal {...cancelModalProps}>
        <ContentLoader
          show={isCancelMutating || isCancelSMSMutating}
          message="Cancelling..."
        />
        <Modal.Header>Are you sure you want to cancel?</Modal.Header>
        <Modal.Actions
          primaryLabel="Yes, cancel."
          onPrimaryClick={cancelPort}
          secondaryLabel="No, do not cancel."
          onSecondaryClick={closeCancelModal}
        />
      </Modal>

      <Modal
        onClose={() => {
          clearSubmitPortingDetails();
          if (submitPortingDetails && !submitPortingDetails?.hasError) {
            onBack();
          }
        }}
        show={!!submitPortingDetails || false}
        css={css`
          padding: ${theme.spacing(2)};
        `}
        className={portingCardStyles.divWidth}
      >
        <div>
          {submitPortingDetails && submitPortingDetails?.hasError && (
            <div>
              <div className={portingCardStyles.fixErrorReason}>
                Porting request failed for the following reasons:
              </div>
              {submitPortingDetails?.errors?.map((error, index) => (
                <div key={index}>{error.description}</div>
              ))}
              <div className={portingCardStyles.fixErrorTitle}>
                Fix these errors and resubmit.
              </div>
              <PrimaryButton
                className={portingCardStyles.errorOkayButton}
                color="blue"
                size="large"
                onClick={() => clearSubmitPortingDetails()}
              >
                Okay
              </PrimaryButton>
            </div>
          )}
          {submitPortingDetails && !submitPortingDetails?.hasError && (
            <div>
              <div className={portingCardStyles.loading}>
                <SuccessSVG />
              </div>
              <div className={portingCardStyles.successSubmit}>
                Port Successfully Submitted!
              </div>
              {port.provider !== Provider.PROVIDER_TELNYX && (
                <div
                  className={portingCardStyles.uploadLoaButton}
                  onClick={() =>
                    handleUploadLoaClick(submitPortingDetails?.orderId ?? '')
                  }
                >
                  Upload LOA
                </div>
              )}
              <PrimaryButton
                className={portingCardStyles.gotItButton}
                color="blue"
                size="large"
                onClick={() => {
                  clearSubmitPortingDetails();
                  onBack();
                }}
              >
                Got it
              </PrimaryButton>
            </div>
          )}
        </div>
      </Modal>
      <Modal
        css={css`
          padding: ${theme.spacing(2)};
        `}
        {...phoneNumbersEditDiscardModalProps}
      >
        <div>
          <h1
            css={css`
              text-align: center;
              margin-bottom: 16px;
            `}
          >
            Are you sure you want to discard the changes?
          </h1>
          <ButtonBar>
            <PrimaryButton onClick={() => cancelPhoneChanges()}>Yes</PrimaryButton>

            <SecondaryButton
              onClick={() => {
                closePhoneNumbersEditDiscardModal();
                setPhoneNumbersEdit(true);
              }}
            >
              No
            </SecondaryButton>
          </ButtonBar>
        </div>
      </Modal>
      {port.id && (
        <SupRequestModal
          minDate={minDate}
          port={port}
          adminEmail={username}
          updatePortDetailsByFoc={updatePortDetailsByFoc}
          {...supModalProps}
        />
      )}
    </div>
  );
};
